import { PLANNER_ANALYTICS_SOURCE } from '@bridebook/web/lib/ai-planner/client-actions/agent-dispatch';
import { interactedWithPlannerAnalytics } from '@bridebook/web/lib/ai-planner/analytics/actions';
import { getPlannerEntryLocation } from '@bridebook/web/lib/ai-planner/analytics/entry-location';
import { useDispatch } from '@bridebook/web/lib/utils';
import { useRouter } from 'next/router';
import { useCallback } from 'react';

/**
 * The planner's interaction events in one place. Each callback fills in the properties
 * for its interaction, so callers only say what happened.
 */
export const usePlannerAnalytics = () => {
  const dispatch = useDispatch();
  // The route pattern, such as `/[market]/checklist/[id]`, not the URL
  const { pathname } = useRouter();

  // The conversational onboarding screen opened.
  const startedOnboardingAnalytics = useCallback(
    () =>
      dispatch(
        interactedWithPlannerAnalytics({
          location: PLANNER_ANALYTICS_SOURCE,
          section: 'onboarding',
        }),
      ),
    [dispatch],
  );

  // The conversational onboarding is over: completed, or left early with the explore button.
  const finishedOnboardingAnalytics = useCallback(
    ({ skipped }: { skipped: boolean }) =>
      dispatch(
        interactedWithPlannerAnalytics(
          skipped
            ? {
                action: 'clicked',
                location: PLANNER_ANALYTICS_SOURCE,
                section: 'onboarding',
                element: 'closeButton',
                elementLabel: 'Explore Bridebook first',
                onboardingCompleted: false,
              }
            : {
                location: PLANNER_ANALYTICS_SOURCE,
                section: 'onboarding',
                onboardingCompleted: true,
              },
        ),
      ),
    [dispatch],
  );

  // The floating button opened the planner, on whichever page the couple is.
  const clickedFloatingButtonAnalytics = useCallback(
    () =>
      dispatch(
        interactedWithPlannerAnalytics({
          action: 'clicked',
          location: getPlannerEntryLocation(pathname),
          section: 'PageSection',
          element: 'icon',
          elementLabel: null,
        }),
      ),
    [dispatch, pathname],
  );

  // The couple closed the planner with the close button or Escape. App-made closes don't count.
  const closedPlannerAnalytics = useCallback(
    (element: 'closeButton' | 'escapeKey') =>
      dispatch(
        interactedWithPlannerAnalytics({
          action: 'clicked',
          location: PLANNER_ANALYTICS_SOURCE,
          section: 'closeButton',
          element,
          elementLabel: null,
        }),
      ),
    [dispatch],
  );

  // The settings switch turned the planner on or off.
  const toggledSettingAnalytics = useCallback(
    (enabled: boolean) =>
      dispatch(
        interactedWithPlannerAnalytics({
          action: 'clicked',
          location: 'settings',
          section: PLANNER_ANALYTICS_SOURCE,
          element: 'toggle',
          elementLabel: null,
          weddingPlannerEnabled: enabled,
        }),
      ),
    [dispatch],
  );

  return {
    startedOnboardingAnalytics,
    finishedOnboardingAnalytics,
    toggledSettingAnalytics,
    clickedFloatingButtonAnalytics,
    closedPlannerAnalytics,
  };
};
